import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Play, Square, Plus, Upload, Trash2, Search, RotateCcw, ListChecks } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { AdminSidebar } from '../../components/AdminSidebar';
import { IvrNav } from '../../components/IvrNav';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { API_URL, BACKEND_URL } from '../../utils/api';
import { getErrorMessage } from '../../utils/errors';

/**
 * The phone line's own audio: every name, title, simcha place, parsha/day line and Hebrew letter it can play,
 * with a play button each. Admins add names and titles here, or replace any clip with an upload
 * (WAV or MP3, for example exported from yiddish.tools). What ships with the IVR stays as the
 * base; changes reach the phone line within a minute (the IVR reloads them).
 */
const PAGES = {
  names: {
    title: 'Names',
    blurb: 'Every first and family name the phone name keypad offers, and how each one sounds. A recording belongs to the spelling, so a name on two lists shares one clip.',
    api: 'names',
    canAdd: true,
    kinds: [
      { value: 'male', label: 'Male' },
      { value: 'female', label: 'Female' },
      { value: 'family', label: 'Family' },
    ],
  },
  titles: {
    title: 'Titles',
    blurb: 'Titles around names (ר\', מו"ה, שליט"א, הי"ו …). The phone plays a title as a word instead of spelling it, however its quote marks were typed.',
    api: 'titles',
    canAdd: true,
    kinds: [{ value: 'title', label: 'Title' }],
  },
  places: {
    title: 'Places',
    blurb: 'Where the simcha is held (בביתי, בביהמ"ד, באולם …), including every place choice in your menus. The phone plays a place\'s recording wherever it says the place back (the menu, "You selected", "You entered" and the order review). A place with no recording is said as before. A hall or shul name typed after a place (באולם ויזניץ) can get a recording of its own: add it here.',
    api: 'places',
    canAdd: true,
    kinds: [{ value: 'place', label: 'Place' }],
  },
  'weeks-and-days': {
    title: 'Weeks & Days',
    blurb: 'The parsha (Simcha Week) and day (Simcha Day) lines the phone plays.',
    api: 'weeks',
    canAdd: false,
    kinds: [
      { value: 'week', label: 'Week' },
      { value: 'day', label: 'Day' },
    ],
  },
  letters: {
    title: 'Letters',
    blurb: 'The Hebrew letter names the phone uses when it spells an answer letter by letter.',
    api: 'letters',
    canAdd: false,
    kinds: [{ value: 'letter', label: 'Letter' }],
  },
};

const PAGE_SIZE = 100;
const MAX_UPLOAD_BYTES = 2 * 1024 * 1024;
const KIND_LABEL = { name: 'Name', male: 'Male', female: 'Female', family: 'Family', title: 'Title', place: 'Place', week: 'Week', day: 'Day', letter: 'Letter' };
// What to call one line on the page (the Add button, the dialog, a missing-text error).
const NOUN = { names: 'name', titles: 'title', places: 'place' };

const lettersOnly = (s) => (s || '').replace(/[֑-ׇ]/g, '').replace(/[^א-תa-z0-9]/gi, '').toLowerCase();
const absolute = (url) => (url && url.startsWith('/') ? `${BACKEND_URL}${url}` : url);

/** Which clip the phone plays for a row, and how to say where it comes from. */
function clipOf(row) {
  if (row.custom_url) return { url: absolute(row.custom_url), label: 'Your upload', tone: 'bg-gold/20 text-ink' };
  if (row.shipped_url) return { url: row.shipped_url, label: 'Built-in clip', tone: 'bg-green-100 text-green-800' };
  if (row.voice_url) return { url: row.voice_url, label: 'IVR voice', tone: 'bg-blue-100 text-blue-800' };
  if (row.kind === 'letter') return { url: null, label: 'Read in English', tone: 'bg-gray-100 text-gray-600' };
  // Places have no IVR voice clip: without a recording the phone says them as it did before.
  if (row.kind === 'place') return { url: null, label: 'No recording yet', tone: 'bg-amber-100 text-amber-800' };
  return { url: null, label: 'IVR voice (being made)', tone: 'bg-gray-100 text-gray-600' };
}

export default function AdminIvrAudio({ page }) {
  const config = PAGES[page];
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState({ loading: true, error: '', reachable: true, listed: true });
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState('all');
  const [shown, setShown] = useState(PAGE_SIZE);
  const [playing, setPlaying] = useState(null);
  const audioRef = useRef(null);
  const [dialog, setDialog] = useState(null); // { mode: 'add'|'replace', row?, text, kind, file }
  const [saving, setSaving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [listsDialog, setListsDialog] = useState(null); // { row, lists: string[] }

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/ivr/audio/${config.api}`, { headers });
      setRows(res.data.rows || []);
      setStatus({ loading: false, error: '', reachable: res.data.ivr_reachable, listed: res.data.ivr_listed });
    } catch (error) {
      setStatus((s) => ({ ...s, loading: false, error: getErrorMessage(error, 'Could not load the list.') }));
    }
  }, [config.api, headers]);

  useEffect(() => {
    setStatus({ loading: true, error: '', reachable: true, listed: true });
    setQuery('');
    setKind('all');
    setShown(PAGE_SIZE);
    load();
  }, [load]);

  // Stop playback when leaving the page.
  useEffect(() => () => audioRef.current?.pause(), []);

  const filtered = useMemo(() => {
    const q = lettersOnly(query);
    return rows.filter((r) => (kind === 'all' || r.kind === kind)
      && (!q || lettersOnly(r.text).includes(q) || lettersOnly(r.key).includes(q)));
  }, [rows, query, kind]);

  useEffect(() => setShown(PAGE_SIZE), [query, kind]);

  const counts = useMemo(() => rows.reduce((acc, r) => ({ ...acc, [r.kind]: (acc[r.kind] || 0) + 1 }), {}), [rows]);

  const play = (row) => {
    const { url } = clipOf(row);
    const id = `${row.kind}|${row.key}`;
    audioRef.current?.pause();
    if (playing === id || !url) {
      setPlaying(null);
      return;
    }
    const audio = new Audio(url);
    audioRef.current = audio;
    setPlaying(id);
    audio.onended = () => setPlaying((p) => (p === id ? null : p));
    audio.play().catch(() => {
      setPlaying(null);
      toast.error('This clip could not be played.');
    });
  };

  const save = async () => {
    if (!dialog) return;
    const file = dialog.file;
    if (file && file.size > MAX_UPLOAD_BYTES) {
      toast.error('That file is too large (up to 2 MB).');
      return;
    }
    if (dialog.mode === 'replace' && !file) {
      toast.error('Choose a WAV or MP3 file to upload.');
      return;
    }
    if (dialog.mode === 'add' && !dialog.text.trim()) {
      toast.error(`Type the ${NOUN[page] || 'name'}.`);
      return;
    }
    const form = new FormData();
    // A clip-only name row (shown during an IVR outage) is saved as a name: its clip is per spelling.
    form.append('kind', dialog.mode === 'replace' ? (dialog.row.kind === 'name' ? 'male' : dialog.row.kind) : dialog.kind);
    form.append('mode', dialog.mode);
    // A name added here: the save checks that its list entry still exists.
    if (dialog.mode === 'replace' && dialog.row.entry_id) form.append('entry_id', dialog.row.entry_id);
    if (dialog.mode === 'replace') {
      form.append('key', dialog.row.key);
      form.append('text', ['week', 'day', 'letter'].includes(dialog.row.kind) ? '' : dialog.row.key);
    } else {
      form.append('text', dialog.text.trim());
    }
    if (file) form.append('file', file);
    setSaving(true);
    try {
      await axios.post(`${API_URL}/admin/ivr/audio`, form, { headers });
      toast.success('Saved. The phone line uses it within a minute.');
      setDialog(null);
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not save.'));
      // 503: the name was added but its recording wasn't saved; 409: changed meanwhile.
      if ([409, 503].includes(error.response?.status)) load();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    try {
      // A built-in line only drops its upload; a line added here is removed (with its upload).
      await axios.delete(`${API_URL}/admin/ivr/audio/${row.shipped ? row.clip_id : row.entry_id}`, { headers });
      toast.success(row.kind === 'place' ? 'Removed.' : row.shipped ? 'Back to the built-in clip.' : 'Removed.');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not remove it.'));
    }
    setConfirmRemove(null);
    load();
  };

  // Which lists a name is on (male / female / family). The recording belongs to the spelling and
  // stays as it is. The page sends what it showed, so an edit made meanwhile elsewhere is caught.
  const saveLists = async (lists) => {
    const { row } = listsDialog;
    const form = new FormData();
    form.append('text', row.key);
    form.append('lists', lists.join(','));
    form.append('expected', (row.lists || []).join(','));
    form.append('version', row.lists_version == null ? '' : String(row.lists_version));
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/ivr/audio/lists`, form, { headers });
      toast.success(lists.length ? 'Saved. The phone line uses it within a minute.' : 'Removed.');
      setListsDialog(null);
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not save.'));
      if (error.response?.status === 409) load();
    } finally {
      setSaving(false);
    }
  };

  const resetLists = async () => {
    setSaving(true);
    try {
      const { row } = listsDialog;
      const form = new FormData();
      form.append('text', row.key);
      form.append('reset', 'true');
      form.append('expected', (row.lists || []).join(','));
      form.append('version', row.lists_version == null ? '' : String(row.lists_version));
      await axios.put(`${API_URL}/admin/ivr/audio/lists`, form, { headers });
      toast.success('Back to its built-in lists.');
      setListsDialog(null);
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not reset.'));
      if (error.response?.status === 409) load();
    } finally {
      setSaving(false);
    }
  };

  const visible = filtered.slice(0, shown);

  return (
    <div className="flex min-h-screen bg-paper">
      <AdminSidebar />
      <div className="ml-64 flex-1 min-w-0 p-4 sm:p-8">
        <h1 className="font-serif text-3xl font-bold text-ink mb-2">IVR Management</h1>
        <p className="text-ink/60 mb-6">Phone-line prompts, field inputs and recordings</p>
        <IvrNav />

        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-4">
          <div>
            <h2 className="text-2xl font-semibold">{config.title}</h2>
            <p className="text-sm text-ink/60 mt-1 max-w-2xl">{config.blurb}</p>
          </div>
          {config.canAdd && (
            <Button onClick={() => setDialog({ mode: 'add', text: '', kind: config.kinds[0].value, file: null })}>
              <Plus className="h-4 w-4 mr-2" />
              {`Add ${NOUN[page] || 'name'}`}
            </Button>
          )}
        </div>

        {!status.loading && !status.reachable && (
          <div role="status" className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            {status.listed
              ? 'The phone line could not be reached just now, so this is the list from a minute ago. Changes still save.'
              : 'The phone line could not be reached, so only what was added or replaced here is shown. Changes still save.'}
          </div>
        )}

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center mb-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink/40" aria-hidden="true" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search"
              aria-label={`Search ${config.title.toLowerCase()}`}
              className="pl-9"
              dir="auto"
            />
          </div>
          {config.kinds.length > 1 && (
            <div className="flex flex-wrap gap-1" role="group" aria-label="Show">
              {[{ value: 'all', label: 'All' }, ...config.kinds].map((k) => (
                <Button
                  key={k.value}
                  size="sm"
                  variant={kind === k.value ? 'default' : 'outline'}
                  aria-pressed={kind === k.value}
                  onClick={() => setKind(k.value)}
                >
                  {k.label} ({k.value === 'all' ? rows.length : counts[k.value] || 0})
                </Button>
              ))}
            </div>
          )}
        </div>

        {status.loading ? (
          <div className="flex justify-center py-20" role="status">
            <div className="animate-spin rounded-full h-12 w-12 border-2 border-gold/20 border-t-gold" />
            <span className="sr-only">Loading</span>
          </div>
        ) : status.error ? (
          <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
            {status.error}{' '}
            <button type="button" className="underline" onClick={() => { setStatus((s) => ({ ...s, loading: true })); load(); }}>
              Try again
            </button>
          </div>
        ) : (
          <>
            <p className="text-xs text-ink/50 mb-2">
              {filtered.length === rows.length ? `${rows.length} in all` : `${filtered.length} of ${rows.length}`}
            </p>
            <ul className="bg-white rounded-lg shadow divide-y divide-ink/5">
              {visible.map((row) => {
                const clip = clipOf(row);
                // A built-in name (even one moved to a list it didn't ship on) can go back to its
                // built-in clip; a name added here can be removed.
                const builtIn = row.shipped || (row.shipped_lists || []).length > 0;
                const id = `${row.kind}|${row.key}`;
                const isPlaying = playing === id;
                return (
                  <li key={id} className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2 sm:px-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => play(row)}
                      disabled={!clip.url}
                      aria-label={isPlaying ? `Stop ${row.text}` : `Play ${row.text}`}
                      className="shrink-0"
                    >
                      {isPlaying ? <Square className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                    </Button>
                    <span dir="rtl" className={`font-hebrew min-w-0 flex-1 ${row.kind === 'letter' ? 'text-2xl' : 'text-lg'}`}>
                      {row.text}
                    </span>
                    <span className="flex flex-wrap items-center gap-1 text-xs">
                      {config.kinds.length > 1 && (
                        <span className="rounded px-2 py-0.5 bg-ink/5 text-ink/70">{KIND_LABEL[row.kind] || row.kind}</span>
                      )}
                      {row.in_menu && <span className="rounded px-2 py-0.5 bg-sky-100 text-sky-800">In your menus</span>}
                      {!builtIn && !row.in_menu && <span className="rounded px-2 py-0.5 bg-purple-100 text-purple-800">Added here</span>}
                      <span className={`rounded px-2 py-0.5 ${clip.tone}`}>{clip.label}</span>
                    </span>
                    <span className="flex gap-1 ml-auto">
                      {page === 'names' && row.lists && row.lists.length > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setListsDialog({ row, lists: [...row.lists] })}
                          aria-label={`Change the lists ${row.text} is on`}
                        >
                          <ListChecks className="h-4 w-4 sm:mr-1" />
                          <span className="hidden sm:inline">Lists</span>
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDialog({ mode: 'replace', row, text: row.text, kind: row.kind, file: null })}
                      >
                        <Upload className="h-4 w-4 sm:mr-1" />
                        <span className="hidden sm:inline">{row.custom_url ? 'Replace' : 'Upload'}</span>
                      </Button>
                      {(builtIn ? row.clip_id : row.entry_id) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setConfirmRemove({ ...row, shipped: builtIn })}
                          aria-label={builtIn ? `Use the built-in clip for ${row.text}` : `Remove ${row.text}`}
                        >
                          {builtIn ? <RotateCcw className="h-4 w-4" /> : <Trash2 className="h-4 w-4 text-red-500" />}
                        </Button>
                      )}
                    </span>
                  </li>
                );
              })}
              {visible.length === 0 && (
                <li className="px-4 py-8 text-center text-sm text-ink/50">
                  {rows.length === 0 ? 'Nothing here yet.' : 'Nothing matches that search.'}
                </li>
              )}
            </ul>
            {filtered.length > shown && (
              <div className="flex justify-center mt-4">
                <Button variant="outline" onClick={() => setShown((n) => n + PAGE_SIZE)}>
                  Show more ({filtered.length - shown} left)
                </Button>
              </div>
            )}
          </>
        )}

        <Dialog open={!!dialog} onOpenChange={(open) => !open && !saving && setDialog(null)}>
          <DialogContent className="max-w-lg">
            {dialog && (
              <>
                <DialogHeader>
                  <DialogTitle>
                    {dialog.mode === 'add'
                      ? `Add a ${NOUN[page] || 'name'}`
                      : `Recording for ${dialog.row.text}`}
                  </DialogTitle>
                  <DialogDescription>
                    {dialog.mode === 'add'
                      ? (page === 'places'
                        ? 'Without a recording the phone line says it as it does now. You can upload one now or later.'
                        : 'Without a recording the phone line says it in its own Yiddish voice. You can upload one now or later.')
                      : 'Upload a WAV or MP3 (up to 15 seconds), for example exported from yiddish.tools. It replaces what the phone plays now.'}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  {dialog.mode === 'add' && (
                    <>
                      <div>
                        <Label htmlFor="ivr-audio-text">{page === 'titles' ? 'Title' : page === 'places' ? 'Place, as callers choose or type it' : 'Name'} (Hebrew letters)</Label>
                        <Input
                          id="ivr-audio-text"
                          dir="rtl"
                          value={dialog.text}
                          maxLength={page === 'places' ? 60 : 20}
                          onChange={(e) => setDialog({ ...dialog, text: e.target.value })}
                          placeholder={page === 'titles' ? 'הרה"ק' : page === 'places' ? 'באולם ויזניץ' : 'יואל'}
                        />
                      </div>
                      {config.kinds.length > 1 && (
                        <div>
                          <Label>List</Label>
                          <div className="flex gap-1 mt-1" role="radiogroup" aria-label="List">
                            {config.kinds.map((k) => (
                              <Button
                                key={k.value}
                                type="button"
                                size="sm"
                                role="radio"
                                aria-checked={dialog.kind === k.value}
                                variant={dialog.kind === k.value ? 'default' : 'outline'}
                                onClick={() => setDialog({ ...dialog, kind: k.value })}
                              >
                                {k.label}
                              </Button>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  <div>
                    <Label htmlFor="ivr-audio-file">Recording{dialog.mode === 'add' ? ' (optional)' : ''}</Label>
                    <Input
                      id="ivr-audio-file"
                      type="file"
                      accept=".wav,.mp3,audio/wav,audio/x-wav,audio/mpeg"
                      onChange={(e) => setDialog({ ...dialog, file: e.target.files?.[0] || null })}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setDialog(null)} disabled={saving}>Cancel</Button>
                  <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={!!listsDialog} onOpenChange={(open) => !open && !saving && setListsDialog(null)}>
          <DialogContent className="max-w-md">
            {listsDialog && (
              <>
                <DialogHeader>
                  <DialogTitle>Lists for <span dir="rtl" className="font-hebrew">{listsDialog.row.text}</span></DialogTitle>
                  <DialogDescription>
                    Choose which lists the phone offers this name on. Its recording stays the same.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-2 py-2">
                  {config.kinds.map((k) => (
                    <label key={k.value} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={listsDialog.lists.includes(k.value)}
                        onChange={(e) => setListsDialog({
                          ...listsDialog,
                          lists: e.target.checked
                            ? [...listsDialog.lists, k.value].sort()
                            : listsDialog.lists.filter((x) => x !== k.value),
                        })}
                      />
                      {k.label}
                      {(listsDialog.row.shipped_lists || []).includes(k.value) && (
                        <span className="text-xs text-ink/50">(built in)</span>
                      )}
                    </label>
                  ))}
                  {listsDialog.lists.length === 0 && (
                    <p className="text-xs text-red-700">
                      {(listsDialog.row.shipped_lists || []).length
                        ? 'A built-in name stays on at least one list.'
                        : 'With no list left, the name and its recording are removed.'}
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                  {listsDialog.row.lists_id && (listsDialog.row.shipped_lists || []).length > 0 && (
                    <Button variant="ghost" onClick={resetLists} disabled={saving} className="mr-auto">
                      <RotateCcw className="h-4 w-4 mr-1" /> Built-in lists
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => setListsDialog(null)} disabled={saving}>Cancel</Button>
                  <Button
                    onClick={() => saveLists(listsDialog.lists)}
                    disabled={saving || (listsDialog.lists.length === 0 && (listsDialog.row.shipped_lists || []).length > 0)}
                  >
                    {saving ? 'Saving…' : listsDialog.lists.length ? 'Save' : 'Remove name'}
                  </Button>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        <AlertDialog open={!!confirmRemove} onOpenChange={(open) => !open && setConfirmRemove(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {confirmRemove?.kind === 'place'
                  ? (confirmRemove?.shipped || confirmRemove?.in_menu ? `Remove the recording of ${confirmRemove?.text}?` : `Remove ${confirmRemove?.text}?`)
                  : confirmRemove?.shipped ? 'Use the built-in clip again?' : `Remove ${confirmRemove?.text}?`}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {confirmRemove?.kind === 'place'
                  ? 'Within a minute the phone line says it as it did before it had a recording.'
                  : confirmRemove?.shipped
                    ? 'Your upload is set aside and the phone plays the clip it came with.'
                    : 'The phone line stops offering it within a minute.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => remove(confirmRemove)}>
                {confirmRemove?.shipped && confirmRemove?.kind !== 'place' ? 'Use built-in clip' : 'Remove'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

export const IVR_AUDIO_PAGES = Object.keys(PAGES);
